/**
 * Observability Service - Centralized runtime data collection for deployed applications.
 * Aggregates logs, metrics, errors, health checks, deployment events, traffic, and alerts.
 */

export type LogLevel = "info" | "warn" | "error" | "debug";
export type HealthStatus = "operational" | "degraded" | "incident";
export type AlertSeverity = "critical" | "high" | "medium" | "low";

export interface ObservabilityLog {
  id: string;
  timestamp: number;
  level: LogLevel;
  service: string;
  message: string;
  metadata?: Record<string, unknown>;
}

export interface PerformanceMetric {
  id: string;
  name: string;
  value: number;
  unit: string;
  timestamp: number;
  service: string;
}

export interface TrackedError {
  id: string;
  message: string;
  stackTrace: string;
  endpoint: string;
  count: number;
  firstSeen: number;
  lastSeen: number;
  status: "open" | "resolved" | "ignored";
}

export interface ServiceHealth {
  service: string;
  status: HealthStatus;
  latencyMs: number;
  uptimePercent: number;
  lastChecked: number;
  checks: { name: string; passed: boolean }[];
}

export interface DeployEvent {
  id: string;
  projectName: string;
  environment: string;
  status: "success" | "failed" | "in_progress" | "rolled_back";
  startedAt: number;
  finishedAt?: number;
  durationMs?: number;
  commit: string;
  logs: string[];
}

export interface TrafficSnapshot {
  timestamp: number;
  requestsPerMin: number;
  uniqueUsers: number;
  topEndpoints: { path: string; hits: number; avgMs: number }[];
  errorRate: number;
}

export interface Alert {
  id: string;
  severity: AlertSeverity;
  title: string;
  message: string;
  service: string;
  triggeredAt: number;
  acknowledged: boolean;
  resolvedAt?: number;
  rule: string;
}

export interface InfraMetrics {
  containers: { id: string; name: string; cpuPercent: number; memoryMb: number; status: string }[];
  dbConnections: number;
  dbQueryAvgMs: number;
  networkLatencyMs: number;
  serverUptime: number;
}

// ── Distributed Tracing ──
export interface TraceSpan {
  traceId: string;
  spanId: string;
  parentSpanId?: string;
  service: string;
  operation: string;
  startTime: number;
  duration: number;
  status: "ok" | "error";
  attributes: Record<string, string | number>;
}

export interface DistributedTrace {
  traceId: string;
  rootService: string;
  rootOperation: string;
  startTime: number;
  totalDuration: number;
  spanCount: number;
  spans: TraceSpan[];
  hasError: boolean;
}

// ── SLOs & Error Budgets ──
export interface SLODefinition {
  id: string;
  name: string;
  target: number; // e.g. 99.9
  type: "availability" | "latency";
  service: string;
  window: "7d" | "30d";
  current: number;
  errorBudgetRemaining: number; // percent
  burnRate: number;
  status: "healthy" | "warning" | "breached";
}

// ── Data Retention ──
export interface RetentionPolicy {
  dataType: string;
  hotDays: number;
  coldDays: number;
  totalRecords: number;
  sizeBytes: number;
}

// ── Mobile Observability ──
export interface MobileCrash {
  id: string;
  device: string;
  os: string;
  version: string;
  message: string;
  stackTrace: string;
  timestamp: number;
  userId?: string;
  count: number;
}

export interface MobilePerformance {
  device: string;
  fps: number;
  batteryDrain: number;
  networkType: string;
  sessionDuration: number;
  anrCount: number;
  crashRate: number;
}

// ── Internal stores ──
let logs: ObservabilityLog[] = [];
let metrics: PerformanceMetric[] = [];
let errors: TrackedError[] = [];
let healthChecks: ServiceHealth[] = [];
let deployments: DeployEvent[] = [];
let trafficHistory: TrafficSnapshot[] = [];
let alerts: Alert[] = [];
const traces: DistributedTrace[] = [];
const slos: SLODefinition[] = [];
const mobileCrashes: MobileCrash[] = [];
const mobilePerformance: MobilePerformance[] = [];
const listeners = new Set<() => void>();
let observabilityVersion = 0;

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function notify() {
  observabilityVersion++;
  listeners.forEach(fn => fn());
}

export function getObservabilityVersion() {
  return observabilityVersion;
}

// ── Public API ──

export function pushLog(level: LogLevel, service: string, message: string, metadata?: Record<string, unknown>) {
  logs = [...logs.slice(-999), { id: uid(), timestamp: Date.now(), level, service, message, metadata }];
  // Check alert rules
  checkAlertRules();
  notify();
}

export function pushMetric(name: string, value: number, unit: string, service: string) {
  metrics = [...metrics.slice(-4999), { id: uid(), name, value, unit, timestamp: Date.now(), service }];
  notify();
}

export function trackError(message: string, stackTrace: string, endpoint: string) {
  const existing = errors.find(e => e.message === message && e.endpoint === endpoint && e.status === "open");
  if (existing) {
    existing.count++;
    existing.lastSeen = Date.now();
  } else {
    errors = [...errors, { id: uid(), message, stackTrace, endpoint, count: 1, firstSeen: Date.now(), lastSeen: Date.now(), status: "open" }];
  }
  notify();
}

export function resolveError(id: string) {
  errors = errors.map(e => e.id === id ? { ...e, status: "resolved" as const } : e);
  notify();
}

export function updateServiceHealth(health: ServiceHealth) {
  healthChecks = healthChecks.filter(h => h.service !== health.service);
  healthChecks.push(health);
  notify();
}

export function pushDeployEvent(event: DeployEvent) {
  deployments = [event, ...deployments].slice(0, 200);
  notify();
}

export function pushTrafficSnapshot(snapshot: TrafficSnapshot) {
  trafficHistory = [...trafficHistory.slice(-999), snapshot];
  notify();
}

export function acknowledgeAlert(id: string) {
  alerts = alerts.map(a => a.id === id ? { ...a, acknowledged: true } : a);
  notify();
}

export function resolveAlert(id: string) {
  alerts = alerts.map(a => a.id === id ? { ...a, resolvedAt: Date.now() } : a);
  notify();
}

// ── Getters ──
export function getLogs(limit = 100, level?: LogLevel, service?: string) {
  let result = logs;
  if (level) result = result.filter(l => l.level === level);
  if (service) result = result.filter(l => l.service === service);
  return result.slice(-limit).reverse();
}

export function getMetrics(name?: string, limit = 200) {
  const result = name ? metrics.filter(m => m.name === name) : metrics;
  return result.slice(-limit);
}

export function getErrors() { return [...errors].sort((a, b) => b.lastSeen - a.lastSeen); }
export function getServiceHealth() { return [...healthChecks]; }
export function getDeployments(limit = 50) { return deployments.slice(0, limit); }
export function getTrafficHistory(limit = 100) { return trafficHistory.slice(-limit); }
export function getAlerts() { return [...alerts].sort((a, b) => b.triggeredAt - a.triggeredAt); }

// ── InfraMetrics: populated by refreshInfraMetrics() from /api/metrics ──

const EMPTY_INFRA: InfraMetrics = {
  containers:       [],
  dbConnections:    0,
  dbQueryAvgMs:     0,
  networkLatencyMs: 0,
  serverUptime:     0,
};

let _infraCache: InfraMetrics = EMPTY_INFRA;

export function getInfraMetrics(): InfraMetrics {
  return _infraCache;
}

export async function refreshInfraMetrics(): Promise<void> {
  try {
    const res = await fetch("/api/metrics", { signal: AbortSignal.timeout(5000) });
    if (!res.ok) return;
    const data = await res.json();
    // Map /api/metrics response (process memory, uptime, service registry) to InfraMetrics
    const mem = data.memory as { rss?: number; heapUsed?: number } | undefined;
    _infraCache = {
      containers: [
        {
          id:         "api-gateway",
          name:       "api-gateway",
          cpuPercent: 0,
          memoryMb:   mem?.heapUsed ? Math.round(mem.heapUsed / 1_048_576) : 0,
          status:     "running",
        },
      ],
      dbConnections:    0,
      dbQueryAvgMs:     0,
      networkLatencyMs: 0,
      serverUptime:     typeof data.uptime === "number" ? data.uptime : 0,
    };
    notify();
  } catch {
    // silent - callers get the last good cache
  }
}

// ── New feature getters ──
export function getTraces(limit = 50): DistributedTrace[]  { return traces.slice(-limit).reverse(); }
export function getSLOs():             SLODefinition[]     { return [...slos]; }
export function getMobileCrashes():    MobileCrash[]       { return [...mobileCrashes].sort((a, b) => b.timestamp - a.timestamp); }

export function pushMobilePerformance(sample: MobilePerformance): void {
  mobilePerformance.push(sample);
  // Keep last 500 samples
  if (mobilePerformance.length > 500) mobilePerformance.splice(0, mobilePerformance.length - 500);
  notify();
}

export function getMobilePerformance(): MobilePerformance[] { return [...mobilePerformance]; }

export function getRetentionPolicies(): RetentionPolicy[] {
  return [
    { dataType: "Logs", hotDays: 30, coldDays: 365, totalRecords: logs.length, sizeBytes: logs.length * 512 },
    { dataType: "Metrics", hotDays: 30, coldDays: 365, totalRecords: metrics.length, sizeBytes: metrics.length * 128 },
    { dataType: "Traces", hotDays: 14, coldDays: 90, totalRecords: traces.length, sizeBytes: traces.length * 2048 },
    { dataType: "Errors", hotDays: 90, coldDays: 365, totalRecords: errors.length, sizeBytes: errors.length * 1024 },
    { dataType: "Alerts", hotDays: 90, coldDays: 365, totalRecords: alerts.length, sizeBytes: alerts.length * 256 },
    { dataType: "Deployments", hotDays: 365, coldDays: 730, totalRecords: deployments.length, sizeBytes: deployments.length * 4096 },
  ];
}

export function exportObservabilityData(dataType: string): string {
  const data = dataType === "logs" ? logs : dataType === "metrics" ? metrics : dataType === "errors" ? errors : dataType === "traces" ? traces : dataType === "alerts" ? alerts : [];
  return JSON.stringify(data, null, 2);
}

export function onObservabilityChange(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ── Alert Rule Engine ──
function checkAlertRules() {
  const recentErrors = logs.filter(l => l.level === "error" && l.timestamp > Date.now() - 60_000);
  if (recentErrors.length >= 5) {
    const alreadyFired = alerts.find(a => a.rule === "high_error_rate" && !a.resolvedAt && a.triggeredAt > Date.now() - 300_000);
    if (!alreadyFired) {
      alerts = [...alerts, {
        id: uid(), severity: "critical", title: "High Error Rate Detected",
        message: `${recentErrors.length} errors in the last 60 seconds`, service: "system",
        triggeredAt: Date.now(), acknowledged: false, rule: "high_error_rate",
      }];
    }
  }
}

// startObservabilitySimulator removed - all data must come from real runtime events.
// Call refreshInfraMetrics() to pull live process metrics from /api/metrics.
// Call pushLog / pushMetric / trackError / pushDeployEvent directly from real code paths.
